import { API, Auth } from 'aws-amplify';
import jwt_decode from 'jwt-decode';

const query = /* GraphQL */ `
  query GetProjects {
    getProjects {
      ... on GetProjectsSuccess {
        __typename
        projects {
          code
          id
          brandId
          permission
          name
          role
        }
      }
      ... on BadRequest {
        __typename
        message
        statusCode
      }
      ... on Forbidden {
        __typename
        message
        statusCode
      }
      ... on InternalServerError {
        __typename
        message
        statusCode
      }
      ... on NotFound {
        __typename
        message
        statusCode
      }
    }
  }
`;

interface DecodedToken {
  'cognito:groups'?: string[];
  groups?: string[];
}

export interface Project {
  code: string;
  id: string;
  brandId: string;
  permission: string;
  name: string;
  role: string[];
}

interface GetProjectsSuccess {
  __typename: 'GetProjectsSuccess';
  projects: Project[];
}

interface ErrorResponse {
  __typename: 'BadRequest' | 'Forbidden' | 'InternalServerError' | 'NotFound';
  message: string;
  statusCode?: number;
}

type GetProjectsResponse = GetProjectsSuccess | ErrorResponse;

async function getProjectsService(): Promise<Project[]> {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const { accessToken } = user.signInUserSession;
    const { jwtToken } = accessToken;
    const decodedToken = jwt_decode<DecodedToken>(jwtToken);

    // Check if the user has any groups
    const hasGroups =
      !!(decodedToken['cognito:groups'] && decodedToken['cognito:groups'].length > 0) ||
      !!(decodedToken.groups && decodedToken.groups.length > 0);

    if (!hasGroups) {
      return [];
    }

    const postData = await API.graphql({
      query,
      authToken: jwtToken,
    }) as { data: { getProjects: GetProjectsResponse } };

    if (!postData.data) {
      throw new Error('Internal Server Error');
    }

    const response = postData.data.getProjects;

    if (response.__typename === 'GetProjectsSuccess') {
      return response.projects;
    }

    throw new Error(response.message);
  } catch (error: any) {
    console.error('Error in getProjectsService:', error);
    throw new Error(`Internal Server Error: ${error.message || 'Unknown error occurred'}`);
  }
}

export default getProjectsService;